import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/store';
import { useToast } from '@/components/Toast';
import {
    UserOutlined,
    MobileOutlined,
    QrcodeOutlined,
    SunOutlined,
    MoonOutlined,
} from '@ant-design/icons';
import {
    Form,
    Button,
    Checkbox,
    message,
    Segmented,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Particles from 'react-particles';
import { loadSlim } from 'tsparticles-slim';
import type { Engine } from 'tsparticles-engine';
import { ParticlesOptions } from '@/config/particles';
import { authService } from '@/services/auth';
import LanguageSwitch from '@/components/LanguageSwitch';
import { useLocale } from '@/hooks/useLocale';
// import logo from '@/assets/logo-方带圆角-QuBIt.png';
// import logo from '@/assets/qubitLogo.svg';
import qubitLogo_en from '@/assets/logo-QuBit.svg';
import qubitLogo_zh from '@/assets/logo-QuBit-zh.svg';
import qubitLogo_zh_tw from '@/assets/logo-QuBit-zh-tw.svg';
import './index.scss';
import AccountLogin from './components/AccountLogin';
import TypeWriter from '@/components/TypeWriter';
import { AnimatePresence, motion } from 'framer-motion';
import { RouteConfig } from '@/types/route';
import PageTransition from '@/components/PageTransition';
import { getSafePass } from '@/utils/encryption';

export const routeConfig: RouteConfig = {
    title: '登录',
    // icon: <StopOutlined />,
    layout: false,
    // hidden: true // 在菜单中隐藏
}

const LoginPage: React.FC = observer(() => {
    const { t } = useTranslation();
    const { currentLang } = useLocale();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { UserStore, ConfigStore } = useStore();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [loginType, setLoginType] = useState<'account' | 'mobile' | 'qrcode'>('account');
    const toast = useToast();

    // Select logo based on current language
    const logo = currentLang === 'zh-CN' 
        ? qubitLogo_zh 
        : currentLang === 'zh-TW' 
        ? qubitLogo_zh_tw 
        : qubitLogo_en;

    // Handle token-based authentication from URL
    useEffect(() => {
        const handleTokenLogin = async () => {
            const token = searchParams.get('token');
            if (token) {
                setTokenLoading(true);
                try {                    
                    let loginResponse;
                    try {
                        // Use affiliate login endpoint with the token
                        const res = await authService.affiliateLogin(token);
                        if (res) {
                            loginResponse = res;
                        }
                    } catch (apiError) {
                        throw apiError;
                    }
                    
                    if (loginResponse) {                        
                        // Create user info object from login response
                        // Note: The token is already stored in authStorage by affiliateLogin method
                        const userInfoWithToken = {
                            id: loginResponse.user_id,
                            username: loginResponse.account,
                            // email: loginResponse.email,
                            inviteCode: loginResponse.invite_code,
                            invitePage: loginResponse.invite_page,
                            rewardRatio: loginResponse.reward_ratio,
                            spotRatio: loginResponse.spot_ratio,
                            permissions: loginResponse.permission,
                            dynamicRoutesList: loginResponse.dynamicRoutesList,
                            // avatar: loginResponse.avatar,
                            accessToken: loginResponse.token // Use the response token, not the URL token
                        };
                        
                        UserStore.setUserInfo(userInfoWithToken);
                        
                        toast.success({ message: t('login.loginSuccess') });
                        
                        // Use a slight delay to ensure state is updated
                        setTimeout(() => {
                            navigate('/', { replace: true });
                        }, 200);
                    } else {
                        throw new Error('No response received from affiliate login');
                    }
                } catch (error: any) {
                    // Check if this is a redirect error (should not show toast in this case)
                    if (error && typeof error === 'object' && 'redirectUrl' in error) {
                        // Don't show error message for redirects, just clear the token
                    } else {
                        // Show error message for non-redirect errors
                        toast.error({ 
                            message: error.errorMessage || error.message || 'Login failed' 
                        });
                    }
                    
                    // Clear the token from URL to prevent infinite retry
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('token');
                    window.history.replaceState({}, document.title, newUrl.toString());
                } finally {
                    setTokenLoading(false);
                }
            }
        };

        handleTokenLogin();
    }, [searchParams, navigate, t, UserStore]);

    // 粒子背景初始化
    const particlesInit = async (engine: Engine) => {
        await loadSlim(engine);
    };

    // 处理登录
    const handleLogin = async (values: any) => {
        setLoading(true);
        try {
            const encryptedPassword = await getSafePass(values.password);
            const response = await authService.login({
                ...values,
                totp_code: values.totp_code || '123456',
                password: encryptedPassword,
                remember: values.remember
            });
            
            if (!response || !response.token) {
                message.error(t('login.loginFailed'));
                return;
            }
            const userInfo = {
                id: response.user_id,
                username: response.account,
                // email: response.email,
                // avatar: response.avatar,
                accessToken: response.token,
                inviteCode: response.invite_code,
                invitePage: response.invite_page,
                rewardRatio: response.reward_ratio,
                spotRatio: response.spot_ratio,
                permissions: response.permission,
                dynamicRoutesList: response.dynamicRoutesList
            };

            UserStore.setUserInfo(userInfo);
            
            message.success(t('login.loginSuccess'));
            navigate('/', { replace: true });
        } catch (error) {
            console.error('Login failed:', error);
            // message.error(t('login.loginFailed'));
        } finally {
            setLoading(false);
        }
    };

    // 渲染登录表单
    const renderLoginForm = () => {
        return (
            <AnimatePresence mode="wait">
                <motion.div
                    key={loginType}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ 
                        duration: 0.3,
                        ease: "easeInOut"
                    }}
                >
                    {loginType === 'account' && <AccountLogin />}
                </motion.div>
            </AnimatePresence>
        );
    };

    // 渲染主要内容
    const renderMainContent = () => {
        return (
            <>
                <div className="header-section">
                    <img src={logo} alt="Logo" className="logo" />
                    {/* <TypeWriter text="QuBit Affiliate" loop={true} loopDelay={2000} /> */}
                    <p className="subtitle mt-2">{t('login.subtitle')}</p>
                </div>

                {/* <Form
                    form={form}
                    onFinish={handleLogin}
                    className="login-form"
                    initialValues={{ remember: true }}
                >
                    {renderLoginForm()}

                    <div className="form-footer">
                        <Form.Item name="remember" valuePropName="checked" noStyle>
                            <Checkbox>{t('login.rememberMe')}</Checkbox>
                        </Form.Item>
                    </div>

                    <Button
                        type="primary"
                        htmlType="submit"
                        className="submit-button"
                        loading={loading}
                    >
                        {t('login.loginButton')}
                    </Button>
                </Form> */}
            </>
        );
    };

    return (
        <PageTransition>
            <div className="login-page">
                <div className="login-container">
                    <Particles
                        className="absolute inset-0"
                        init={particlesInit}
                        options={ParticlesOptions}
                    />

                    {tokenLoading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                <p className="text-gray-700 dark:text-gray-300">
                                    {t('login.processingToken')}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="absolute top-4 right-4 flex items-center space-x-3">
                        <LanguageSwitch />
                        {/* <button
                            className="theme-switch"
                            onClick={() => ConfigStore.setThemeMode(ConfigStore.isDark ? 'light' : 'dark')}
                        >
                            {ConfigStore.isDark ? (
                                <SunOutlined className="text-amber-500" />
                            ) : (
                                <MoonOutlined className="text-blue-500" />
                            )}
                        </button> */}
                    </div>

                    <div className="login-form-container">
                        {renderMainContent()}
                    </div>

                    <div className="copyright">
                        Copyright © {new Date().getFullYear()} QuBit Affiliate
                    </div>
                </div>
            </div>
        </PageTransition>
    );
});

export default LoginPage;